import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import SelectController from "../../forms/SelectController";
import TextField from "../../forms/TextField";
import ControlledSelectField from "../../forms/ControlledSelectField";

export default function RecallFileDialog({
  fileData,
  loading,
  documentLoaded = true,
  onRecallSave,
  onRecallTempSave,
}) {
  // Until the editor has finished loading the document, saving (or temp-saving)
  // would export a blank docx. Disable both actions until then.
  const saveDisabled = loading || !documentLoaded;
  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      recallAmount: fileData?.recallAmount ?? 1,
      recallPeriod: fileData?.recallPeriod ?? "years",
      responsibleUser:
        fileData?.responsibleUser?._id ?? fileData?.responsibleUser ?? "",
    },
  });

  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    reset({
      recallAmount: fileData?.recallAmount ?? 1,
      recallPeriod: fileData?.recallPeriod ?? "years",
      responsibleUser:
        fileData?.responsibleUser?._id ?? fileData?.responsibleUser ?? "",
    });
  }, [fileData, reset]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = async (formData) => {
    const success = await onRecallSave(formData);
    if (success) {
      handleClose();
    }
  };

  const onTempSave = async () => {
    if (!onRecallTempSave) {
      return;
    }
    await onRecallTempSave();
  };

  return (
    <React.Fragment>
      <Button
        disabled={saveDisabled}
        sx={{ mr: 2 }}
        onClick={onTempSave}
        variant="contained"
        color="success"
      >
        ZWISCHENSPEICHERN
      </Button>
      <Button
        variant="contained"
        color="warning"
        disabled={loading}
        onClick={handleClickOpen}
      >
        {loading ? "LADEN..." : "PRÜFEN"}
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="recall-dialog-title"
        aria-describedby="recall-dialog-description"
        maxWidth="lg"
      >
        <DialogTitle id="recall-dialog-title">
          {`${fileData?.fileName ?? ""} prüfen`}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Bitte geben Sie den Zeitraum für die nächste Gültigkeitsprüfung und
            den verantwortlichen Benutzer an.
          </Typography>
          <br />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                name="recallAmount"
                label="Gültigkeitsprüfung in"
                type="number"
                required={true}
                errorMsg="Bitte angeben"
                control={control}
                helperText="Nur Zahlen eingeben"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ControlledSelectField
                name="recallPeriod"
                label="Zeitraum"
                required={true}
                errorMsg="Bitte angeben"
                control={control}
                itemArray={[
                  { label: "Tage", value: "days" },
                  { label: "Wochen", value: "weeks" },
                  { label: "Monate", value: "months" },
                  { label: "Jahre", value: "years" },
                ]}
                helperText="Der Zeitraum, wann an das Dokument erinnert werden soll."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SelectController
                control={control}
                name="responsibleUser"
                label="Verantwortlich"
                errorMsg="Bitte Mitarbeiter auswählen."
                assignToMeQ={false}
                required={true}
                filterUsers={["Editor"]}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={saveDisabled}
            onClick={onTempSave}
            variant="outlined"
            color="warning"
          >
            ZWISCHENSPEICHERN
          </Button>
          <Button
            disabled={saveDisabled}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="warning"
            autoFocus
          >
            PRÜFUNG SPEICHERN
          </Button>
          <Button onClick={handleClose}>Abbrechen</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
